import React, { memo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { FaqCategory } from '../../pages/contactUs/faqs';
import CustomExpandIcon from '../icons/custom-expand-icon';
import { clickedAccordionMenu } from '../mixPanelEvents/mixPanelEvents';

const summarySx: SxProps<Theme> = {
  px: 0,
  py: '16px',
  '& .MuiAccordionSummary-expandIconWrapper': {
    mr: '10px',
  },
};

const categoryTitleSx: SxProps<Theme> = {
  color: '#7B726C',
  fontFamily: '"Repro", sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '150%',
};

const questionTextSx: SxProps<Theme> = {
  color: '#7B726C',
  fontFamily: '"Repro", sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '150%',
};

const answerTextSx: SxProps<Theme> = {
  color: 'var(--Primary-Black, #000)',
  fontFamily: '"Repro", sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '150%',
};

const nestedAccordionSx: SxProps<Theme> = {
  boxShadow: 'none',
  '&:before': { display: 'none' },
  borderTop: '1px solid #666',
  borderBottom: 'none',
  '&:last-child': {
    borderBottom: 'none',
  },
};

const nestedSummarySx: SxProps<Theme> = {
  px: '16px',
  py: '12px',
  '& .MuiAccordionSummary-expandIconWrapper': {
    mr: '10px',
  },
};

const nestedDetailsSx: SxProps<Theme> = {
  px: '16px',
  pb: '12px',
};

type Props = {
  items: readonly FaqCategory[];
};

const FaqAccordion: React.FC<Props> = ({ items }) => {
  const handleCategoryChange = (categoryTitle: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      clickedAccordionMenu('Contact Us', categoryTitle, 'None');
    }
  };

  const handleItemChange = (categoryTitle: string, itemQuestion: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      clickedAccordionMenu('Contact Us', categoryTitle, itemQuestion);
    }
  };

  return (
    <Box sx={{ mt: '16px', alignSelf: 'stretch', width: '100%' }}>
      {items.map((category, categoryIndex) => (
        <Accordion
          key={category.id}
          disableGutters
          square
          onChange={handleCategoryChange(category.title)}
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            borderTop: categoryIndex === 0 ? '1px solid #7B726C' : 'none',
            borderBottom: '1px solid #7B726C',
            mb: '8px',
          }}
        >
          <AccordionSummary expandIcon={<CustomExpandIcon />} sx={summarySx}>
            <Typography sx={categoryTitleSx}>{category.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pb: 0 }}>
            <Box sx={{ 
              width: '100%',
              padding: '20px',
              background: '#FCFAFA'
            }}>
              {category.items.map((item, itemIndex) => (
                <Accordion
                  key={item.id}
                  disableGutters
                  square
                  onChange={handleItemChange(category.title, item.question)}
                  sx={nestedAccordionSx}
                >
                  <AccordionSummary expandIcon={<CustomExpandIcon />} sx={nestedSummarySx}>
                    <Typography sx={questionTextSx}>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={nestedDetailsSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        color: 'var(--Primary-Black, #000)',
                        fontFamily: '"Repro", sans-serif',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '150%'
                      }}
                    >
                      {item.answer}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default memo(FaqAccordion);